import React from 'react';
import {
  Alert, Badge, Button, ErrorState, Table,
} from '@parsina/framework';
import { ArrowRight } from 'lucide-react';

import { useTranslation, MIRROR_ICON } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { EMPTY } from '../../lib/format';
import { num } from '../../lib/aiPricingView';
import { VISIT_QUERY } from './queries';
import { InvocationDetail } from './InvocationDrawer';
import {
  BlockSkeleton, Code, CountCell, DetailDrawer, Fact, MarginBadge, ParsehCell, UsdCell,
  useEnumLabel, useServiceLabel,
} from './shared';
import {
  EarnedCell, PaidCell, useEarnedUnit, usePaidUnit, useVisitMoney,
} from './VisitCells';

const STATUS_VARIANT = { ok: 'success', error: 'destructive', aborted: 'warning' };

/**
 * One visit (PM-0427): its figures, its features and its runs, at
 * `?visit=<appointment id>`.
 *
 * Opening a run does NOT open a second drawer — the overlay law allows one
 * overlay plus a confirm (DESIGN_CONCEPT §5.2). The SAME drawer swaps its body
 * to the run (`?inv=<request id>`, the ledger's own `InvocationDetail`) with a
 * «بازگشت به ویزیت» that puts the visit back; Esc still closes the drawer.
 */
export default function VisitDrawer({ appointmentId, policyId, inv, onOpenInvocation, onBack, onClose, labelOf }) {
  const { t } = useTranslation();
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [invService, setInvService] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(VISIT_QUERY, { appointmentId, policyId: policyId || null })
      .then((d) => setDetail(d?.aiVisit || null))
      .catch((e) => setError({ message: errorMessage(e, t), notFound: e?.code === 'NOT_FOUND' }))
      .finally(() => setLoading(false));
  }, [appointmentId, policyId, t]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { if (!inv) setInvService(null); }, [inv]);

  const title = inv
    ? (invService ? labelOf(invService) : t('aiPricing.ledger.drawer.title'))
    : t('aiPricing.visits.drawer.title');

  return (
    <DetailDrawer
      open
      onClose={onClose}
      width={720}
      title={title}
      sub={<Code>{inv || appointmentId}</Code>}
      data-testid="ai-visit-drawer"
    >
      {inv ? (
        <div className="flex flex-col gap-4" data-testid="ai-visit-drawer-invocation">
          <div>
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="ai-visit-drawer-back">
              {/* Directional: it points back along the reading order, so it mirrors. */}
              <ArrowRight size={14} aria-hidden style={MIRROR_ICON} />
              {t('aiPricing.visits.drawer.back')}
            </Button>
          </div>
          <InvocationDetail
            requestId={inv}
            onLoaded={(row) => setInvService(row?.serviceKey || null)}
            testId="ai-visit-drawer-invocation"
          />
        </div>
      ) : loading ? (
        <div data-state="loading" role="status" aria-busy="true" aria-label={t('aiPricing.visits.drawer.loading')} data-testid="ai-visit-drawer-loading">
          <BlockSkeleton lines={8} height={18} />
        </div>
      ) : error || !detail ? (
        <ErrorState
          title={error?.notFound ? t('aiPricing.visits.drawer.notFound') : t('aiPricing.visits.drawer.error')}
          description={error?.notFound ? t('aiPricing.visits.drawer.notFoundDesc') : (error?.message || t('errors.region.noAnswer'))}
          onRetry={error?.notFound ? undefined : load}
          retryLabel={t('common.actions.retry')}
          data-testid="ai-visit-drawer-error"
        />
      ) : (
        <VisitBody detail={detail} labelOf={labelOf} onOpenInvocation={onOpenInvocation} />
      )}
    </DetailDrawer>
  );
}

function VisitBody({ detail, labelOf, onOpenInvocation }) {
  const { t, formatDateTime, formatNumber } = useTranslation();
  const ownerLabel = useEnumLabel('aiPricing.overview.owner');
  const statusLabel = useEnumLabel('aiPricing.ledger.status');
  const reasonLabel = useEnumLabel('aiPricing.ledger.chargeReason');
  const serviceLabel = useServiceLabel();
  const conv = useVisitMoney(detail.money);
  const earnedUnit = useEarnedUnit(conv);
  const paidUnit = usePaidUnit(conv);
  const v = detail.visit;
  const minutes = (x) => (num(x) == null ? EMPTY : formatNumber(num(x), { maximumFractionDigits: 1 }));

  return (
    <div className="flex flex-col gap-6" data-state="content" data-testid="ai-visit-drawer-content">
      <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3" data-testid="ai-visit-drawer-facts">
        <Fact label={t('aiPricing.ledger.drawer.appointment')} value={v.appointmentId} ltr />
        <Fact
          label={t('aiPricing.ledger.drawer.owner')}
          value={(
            <span className="inline-flex flex-wrap items-center gap-1">
              {ownerLabel(v.ownerType)} <Code>{v.ownerId}</Code>
              {(v.owners || 0) > 1 ? <Badge variant="warning">{t('aiPricing.visits.multiOwner', { count: v.owners })}</Badge> : null}
            </span>
          )}
        />
        <Fact label={t('aiPricing.visits.drawer.first')} value={formatDateTime(v.firstAt)} />
        <Fact label={t('aiPricing.visits.drawer.last')} value={v.lastAt ? formatDateTime(v.lastAt) : EMPTY} />
        <Fact label={t('aiPricing.ledger.drawer.billedMinutes')} value={minutes(v.billedMinutes)} />
        <Fact label={t('aiPricing.visits.drawer.recordings')} value={formatNumber(v.recordings ?? 0)} />
        <Fact label={t('aiPricing.visits.drawer.runs')} value={t('aiPricing.visits.drawer.runsValue', { runs: formatNumber(v.runs ?? 0), charged: formatNumber(v.chargedRuns ?? 0) })} />
        <Fact label={t('aiPricing.visits.drawer.earned', { unit: earnedUnit })} value={<EarnedCell parseh={v.earnedParseh} conv={conv} />} />
        <Fact label={t('aiPricing.visits.drawer.earnedUsd')} value={<UsdCell value={v.earnedUsd} />} />
        <Fact label={t('aiPricing.visits.drawer.paid', { unit: paidUnit })} value={<PaidCell usd={v.paidUsd} conv={conv} partial={(v.unpricedRuns || 0) > 0} />} />
        <Fact label={t('aiPricing.visits.drawer.providerPaid')} value={<UsdCell value={v.providerPaidUsd} />} />
        <Fact label={t('aiPricing.visits.table.columns.margin')} value={<MarginBadge value={v.marginPct} />} />
        {(v.unpricedRuns || 0) > 0 ? (
          <Fact label={t('aiPricing.visits.drawer.unpriced')} value={formatNumber(v.unpricedRuns)} />
        ) : null}
      </dl>

      <section>
        <h3 className="mb-2 text-sm font-bold text-foreground">{t('aiPricing.visits.drawer.lines')}</h3>
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table
            dense
            ariaLabel={t('aiPricing.visits.drawer.linesAria')}
            rows={detail.lines || []}
            rowKey={(l) => l.serviceKey}
            rowProps={(l) => ({ 'data-testid': `ai-visit-drawer-line-${l.serviceKey}` })}
            columns={[
              {
                key: 'service',
                header: t('aiPricing.visits.features.columns.feature'),
                render: (l) => (
                  <div className="min-w-0">
                    <div className="truncate text-foreground">{serviceLabel(l)}</div>
                    <div className="truncate text-xs text-muted-foreground"><Code>{l.serviceKey}</Code></div>
                  </div>
                ),
              },
              {
                key: 'runs',
                header: t('aiPricing.visits.table.columns.runs'),
                align: 'end',
                render: (l) => (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    <CountCell value={l.runs} />
                    <span className="text-[11px] text-muted-foreground">{t('aiPricing.visits.table.charged', { count: l.chargedRuns ?? 0 })}</span>
                  </span>
                ),
              },
              { key: 'minutes', header: t('aiPricing.visits.table.columns.minutes'), align: 'end', render: (l) => <span className="tabular-nums">{minutes(l.billedMinutes)}</span> },
              { key: 'earned', header: t('aiPricing.visits.table.columns.earned', { unit: earnedUnit }), align: 'end', render: (l) => <EarnedCell parseh={l.earnedParseh} conv={conv} /> },
              { key: 'paid', header: t('aiPricing.visits.table.columns.paid', { unit: paidUnit }), align: 'end', render: (l) => <PaidCell usd={l.paidUsd} conv={conv} partial={(l.unpricedRuns || 0) > 0} /> },
              { key: 'provider', header: t('aiPricing.visits.drawer.providerPaidShort'), align: 'end', render: (l) => <UsdCell value={l.providerPaidUsd} /> },
            ]}
            empty={<p className="p-4 text-sm text-muted-foreground">{t('aiPricing.overview.breakdownEmpty')}</p>}
            data-testid="ai-visit-drawer-lines"
          />
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-bold text-foreground">{t('aiPricing.visits.drawer.invocations')}</h3>
        {detail.invocationsTruncated ? (
          <Alert variant="info" className="mb-2" data-testid="ai-visit-drawer-truncated">
            {t('aiPricing.visits.drawer.truncated', { count: (detail.invocations || []).length })}
          </Alert>
        ) : null}
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table
            dense
            ariaLabel={t('aiPricing.visits.drawer.invocationsAria')}
            rows={detail.invocations || []}
            rowKey={(r) => r.requestId}
            onRowActivate={(r) => onOpenInvocation(r.requestId)}
            rowProps={(r) => ({ 'data-testid': `ai-visit-drawer-inv-${r.requestId}`, 'aria-label': t('aiPricing.ledger.open', { id: r.requestId }) })}
            columns={[
              { key: 'time', header: t('aiPricing.ledger.columns.time'), width: '10rem', render: (r) => <span className="tabular-nums">{formatDateTime(r.startedAt)}</span> },
              { key: 'service', header: t('aiPricing.ledger.columns.service'), render: (r) => <span className="truncate">{labelOf(r.serviceKey)}</span> },
              { key: 'cost', header: t('aiPricing.ledger.columns.cost'), align: 'end', render: (r) => <UsdCell value={r.costUsd} /> },
              {
                key: 'charged',
                header: t('aiPricing.ledger.columns.charged'),
                align: 'end',
                render: (r) => (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    <ParsehCell value={r.chargedParseh} />
                    {num(r.chargedParseh) === 0 && r.chargeReason && r.chargeReason !== 'charged' ? (
                      <Badge variant={r.chargeReason === 'wallet_error' ? 'destructive' : 'outline'}>{reasonLabel(r.chargeReason)}</Badge>
                    ) : null}
                  </span>
                ),
              },
              { key: 'status', header: t('aiPricing.ledger.columns.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'outline'}>{statusLabel(r.status)}</Badge> },
            ]}
            empty={<p className="p-4 text-sm text-muted-foreground">{t('aiPricing.overview.breakdownEmpty')}</p>}
            data-testid="ai-visit-drawer-invocations"
          />
        </div>
      </section>
    </div>
  );
}
